import { createContext, type PropsWithChildren, useContext } from 'react';
import { compose, type Nullable } from '@gov-nx/core/types';
import { type PPPZapisContextTypes } from './service.types';
import {
	useFileUpload,
	usePersonCommunicationData,
	useRezaSwitcher,
	useServiceAuthorization,
	useWizard,
} from '@gov-nx/core/hooks';
import { useFormStep1 } from './formSteps/useFormStep1';
import { useFormStep2 } from './formSteps/useFormStep2';
import type { PPPZapisForm } from './formSteps/formSteps.types';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { prepareSubmitData } from './prepareSubmitData';
import { PPPTypeOptions, PPPZapisUploadParameters } from './constants';
import { resolveIsPPPOvmSearchVisible } from './utils';
import { useFormStep3 } from './formSteps/useFormStep3';
import { useMessageEvents } from '@gov-nx/core/events';
import { usePPPLocale } from '../usePPPLocale';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import { usePPPZapisRecapValues } from './hooks/usePPPZapisRecapValues';
import type { ServiceCode } from '../..';

export const PPPZapisContext =
	createContext<Nullable<PPPZapisContextTypes>>(null);

const AUTHORIZATION_SERVICE_NAME = 'PODANI_PPP_ZAPIS';

export const PPPZapisContextProvider = ({
	children,
	code,
}: PropsWithChildren<{ code: ServiceCode }>) => {
	const { t } = usePPPLocale();
	const { toastMessageSuccess } = useMessageEvents();
	const personCommunicationData = usePersonCommunicationData();
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const rezaSwitcher = useRezaSwitcher({ code });

	const mutation = usePoMutation({
		mutationKey: rezaSwitcher.blockingMutationKey,
		mutationFn: compose(sendSubmissionQuery(axiosInstance), prepareSubmitData),
		onSuccess: (data) => {
			wizard.resetForms();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('zapis.form.message.odeslano'));
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					// @ts-expect-error - compose does return correct types
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const formStep1 = useFormStep1({
		isDisabled: mutation.isLoading,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});
	const formStep2 = useFormStep2(mutation.isLoading);
	const formStep3 = useFormStep3(mutation.isLoading, isLowAuthorization);

	const PPPSelect = formStep2.formMethods.watch('PPPSelect');
	const isPodnetKDoplneniSeznamu =
		PPPSelect === PPPTypeOptions.PodnetKDoplneniSeznamu;
	const pppTypeCode = PPPSelect;

	const fileUploadHook = useFileUpload(
		formStep2.formMethods,
		'fileUpload',
		PPPZapisUploadParameters
	);

	const wizard = useWizard<PPPZapisForm>({
		steps: [formStep1, formStep2, formStep3],
		onStepInvalid: (index, errors) => {
			if (index === 0) {
				personCommunicationData.onFormValidated(errors);
			}
		},
		onSubmit: async (data) => mutation.mutate(data),
	});

	const zapisRecapValues = usePPPZapisRecapValues({
		formData: wizard.formData,
		personIdentity: rezaSwitcher.personIdentity,
	});

	return (
		<PPPZapisContext.Provider
			value={{
				formStep1,
				formStep2,
				wizard,
				rezaSwitcher,
				personCommunicationData,
				fileUploadHook,
				isLowAuthorization,
				isAuthorizationEnforced,
				isOVMDefunct: formStep2.formMethods.watch('isOVMDefunct'),
				isPodnetKDoplneniSeznamu,
				pppTypeCode,
				isPPPOvmSearchVisible: resolveIsPPPOvmSearchVisible(pppTypeCode),
				isSubmitting: mutation.isLoading,
				zapisRecapValues,
			}}>
			{children}
		</PPPZapisContext.Provider>
	);
};

export const usePPPZapisContext = (): PPPZapisContextTypes =>
	useContext(PPPZapisContext) as PPPZapisContextTypes;
